import { Flex, Group, rem, SimpleGrid, Skeleton, Stack } from "@mantine/core";
import { CatalogGridSkeleton } from "../CatalogGrid/skeleton";
import { IntegrationCard, IntegrationPage } from "./index";

/**
 * Esqueleto das telas de detalhe, uma peça só para os dois casos: a diferença
 * entre "ainda não instalada" e "instalada" é o botão do cabeçalho e o cartão
 * de destaque, o resto é o mesmo empilhamento.
 *
 * As alturas seguem o que a tela carregada mede de verdade (cartões de 130 a
 * 460px, medidos com a página em 1200px). O esqueleto antigo eram três blocos
 * de 100px: a página crescia uns 800px de uma vez quando o dado chegava, e o
 * que a pessoa via piscar não tinha nada a ver com o que ia aparecer.
 */

/** Linhas de um parágrafo. A última mais curta, como texto de verdade quebra. */
function LinhasDeTexto({ larguras }: { larguras: (string | number)[] }) {
  return (
    <Stack gap={rem(8)}>
      {larguras.map((largura, index) => (
        <Skeleton
          key={index}
          height={rem(16)}
          width={largura}
          radius="sm"
        />
      ))}
    </Stack>
  );
}

/** Grade de ações: ícone em tile, nome curto e uma frase, em duas colunas. */
function GradeDeAcoes({ linhas }: { linhas: number }) {
  return (
    <SimpleGrid
      cols={{ base: 1, sm: 2 }}
      spacing={rem(28)}
      verticalSpacing={rem(16)}>
      {Array.from({ length: linhas * 2 }).map((_, index) => (
        <Group
          key={index}
          gap={rem(11)}
          align="flex-start"
          wrap="nowrap">
          <Skeleton
            height={rem(30)}
            width={rem(30)}
            radius="md"
          />
          <Stack
            gap={rem(6)}
            flex={1}>
            <Skeleton
              height={rem(15)}
              width="58%"
              radius="sm"
            />
            {/* Duas linhas: a frase de uma ação quase nunca cabe em uma. */}
            <Skeleton
              height={rem(14)}
              radius="sm"
            />
            <Skeleton
              height={rem(14)}
              width="84%"
              radius="sm"
            />
          </Stack>
        </Group>
      ))}
    </SimpleGrid>
  );
}

/**
 * Painel da conta conectada: duas colunas, a da esquerda com identificação e a
 * da direita com a janela de atendimento. É o maior bloco da tela instalada, e
 * como retângulo único virava uma parede cinza de 340px.
 */
function PainelDaConta() {
  return (
    <IntegrationCard>
      <SimpleGrid
        cols={{ base: 1, sm: 2 }}
        spacing={rem(28)}>
        <Stack gap={rem(14)}>
          <Skeleton
            height={rem(12)}
            width={rem(130)}
            radius="sm"
          />
          <Skeleton
            height={rem(20)}
            width={rem(180)}
            radius="sm"
          />
          <LinhasDeTexto larguras={["100%", "96%", "72%"]} />
          <Group gap={rem(28)}>
            {[rem(160), rem(130)].map((largura) => (
              <Stack
                key={largura}
                gap={rem(6)}>
                <Skeleton
                  height={rem(11)}
                  width={rem(90)}
                  radius="sm"
                />
                <Skeleton
                  height={rem(14)}
                  width={largura}
                  radius="sm"
                />
              </Stack>
            ))}
          </Group>
          <Skeleton
            height={rem(14)}
            width={rem(170)}
            radius="sm"
          />
        </Stack>
        {/* A janela de atendimento vive num painel próprio dentro do cartão. */}
        <Skeleton
          height={rem(250)}
          radius="md"
        />
      </SimpleGrid>
    </IntegrationCard>
  );
}

export function IntegrationDetailSkeleton({
  /** Instalada: duas ações no cabeçalho e o painel da conta no lugar do "Sobre". */
  instalada = false,
}: {
  instalada?: boolean;
}) {
  return (
    <Stack gap={rem(24)}>
      <Skeleton
        height={rem(19)}
        width={rem(190)}
        radius="sm"
      />

      <Flex
        justify="space-between"
        align="flex-start"
        gap={rem(16)}
        wrap="wrap">
        <Group
          gap={rem(16)}
          align="flex-start"
          wrap="nowrap">
          <Skeleton
            height={rem(56)}
            width={rem(56)}
            radius="md"
          />
          <Stack gap={rem(6)}>
            <Skeleton
              height={rem(26)}
              width={rem(200)}
              radius="sm"
            />
            <Skeleton
              height={rem(14)}
              width={rem(240)}
              radius="sm"
            />
            <Skeleton
              height={rem(12)}
              width={rem(300)}
              radius="sm"
            />
          </Stack>
        </Group>
        {instalada ? (
          <Group gap={rem(8)}>
            <Skeleton
              height={rem(36)}
              width={rem(140)}
              radius="md"
            />
            <Skeleton
              height={rem(36)}
              width={rem(118)}
              radius="md"
            />
          </Group>
        ) : (
          <Skeleton
            height={rem(42)}
            width={rem(224)}
            radius="md"
          />
        )}
      </Flex>

      {instalada ? (
        <PainelDaConta />
      ) : (
        <IntegrationCard>
          <Skeleton
            height={rem(20)}
            width={rem(200)}
            radius="sm"
          />
          <LinhasDeTexto larguras={["100%", "68%"]} />
        </IntegrationCard>
      )}

      <IntegrationCard>
        <Stack gap={rem(6)}>
          <Skeleton
            height={rem(20)}
            width={rem(250)}
            radius="sm"
          />
          <Skeleton
            height={rem(14)}
            width={rem(330)}
            radius="sm"
          />
        </Stack>
        <GradeDeAcoes linhas={instalada ? 2 : 3} />
      </IntegrationCard>

      <IntegrationCard>
        <Stack gap={rem(6)}>
          <Skeleton
            height={rem(20)}
            width={rem(280)}
            radius="sm"
          />
          <Skeleton
            height={rem(14)}
            width={rem(420)}
            radius="sm"
          />
        </Stack>
        {instalada ? (
          /* Bloco do prompt injetado: é um retângulo de texto monoespaçado. */
          <Skeleton
            height={rem(300)}
            radius="md"
          />
        ) : (
          <LinhasDeTexto larguras={["76%", "64%", "70%"]} />
        )}
      </IntegrationCard>
    </Stack>
  );
}

/**
 * Fallbacks de rota, enquanto o chunk da página baixa.
 *
 * Moram aqui, e não no `AppRoutes`, porque precisam do MESMO shell da página:
 * montados num `Stack p="xl"` solto, o esqueleto abria com a largura inteira
 * da janela e a página encolhia para 1200px assim que o chunk chegava. Num
 * monitor grande o salto era de mais de 600px de largura.
 */
export function IntegrationsCatalogFallback() {
  return (
    <IntegrationPage>
      <CatalogGridSkeleton />
    </IntegrationPage>
  );
}

export function IntegrationDetailFallback() {
  return (
    <IntegrationPage>
      <IntegrationDetailSkeleton />
    </IntegrationPage>
  );
}
